import React, { useState, useEffect, useRef } from 'react';
import {
    View,
    Text,
    ScrollView,
    Image,
    TouchableOpacity,
    StyleSheet,
    ActivityIndicator,
    Linking,
    Dimensions,
    Animated,
    Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getBusinessDetails } from '../../api/marketplace';
import { useCart } from '../../context/CartContext';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { StatusBar } from 'expo-status-bar';
import { H2, H3, Caption } from '../../components/common/TextStyles';
import { getShadow } from '../../theme/useTheme';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');
import { getImageUrl } from '../../utils/imageUtils';

import MarketplaceFooter from '../../components/common/MarketplaceFooter';

const BusinessDetailScreen = ({ route, navigation }) => {
    const { listingId } = route.params;
    const { addToCart, getCartCount } = useCart();
    const insets = useSafeAreaInsets();
    const scrollY = useRef(new Animated.Value(0)).current;

    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [collapsedCategories, setCollapsedCategories] = useState({});
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    useEffect(() => {
        fetchListingDetails();
    }, [listingId]);

    const fetchListingDetails = async () => {
        try {
            setLoading(true);
            const data = await getBusinessDetails(listingId);
            setListing(data);
        } catch (error) {
            console.error('Error fetching listing details:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (category) => {
        setCollapsedCategories(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    const handleContact = (method) => {
        if (!listing) return;
        if (method === 'phone') {
            Linking.openURL(`tel:${listing.phone}`);
        } else if (method === 'whatsapp') {
            Linking.openURL(`whatsapp://send?phone=${listing.whatsapp}&text=Hi, I saw your business on Youth Connect!`);
        } else if (method === 'email') {
            Linking.openURL(`mailto:${listing.email}`);
        }
    };

    const renderItemImages = (images) => {
        if (!images) return 'https://via.placeholder.com/150';
        try {
            const parsed = typeof images === 'string' ? JSON.parse(images) : images;
            const target = (Array.isArray(parsed) && parsed.length > 0) ? parsed[0] : images;
            return getImageUrl(target) || 'https://via.placeholder.com/150';
        } catch (e) {
            return getImageUrl(images) || 'https://via.placeholder.com/150';
        }
    };

    const groupedItems = listing ? listing.items.reduce((acc, item) => {
        const cat = item.category || 'General';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(item);
        return acc;
    }, {}) : {};

    const ITEM_CATEGORIES = {
        'General': { icon: 'grid-outline', color: '#64748B' },
        'Electronics': { icon: 'hardware-chip-outline', color: '#F59E0B' },
        'Clothing': { icon: 'shirt-outline', color: '#EC4899' },
        'Food': { icon: 'restaurant-outline', color: '#EF4444' },
        'Home': { icon: 'home-outline', color: '#10B981' },
        'Beauty': { icon: 'sparkles-outline', color: '#F472B6' },
        'Books': { icon: 'book-outline', color: '#8B5CF6' },
        'Services': { icon: 'construct-outline', color: '#6366F1' },
        'Custom': { icon: 'color-palette-outline', color: '#3B82F6' }
    };

    if (loading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#6366F1" />
            </View>
        );
    }

    if (!listing) return null;

    const gap = 8;
    const padding = 16;
    const cardWidth = (SCREEN_WIDTH - (padding * 2) - (gap * 2)) / 3.2;

    const headerHeight = 180;
    const headerOpacity = scrollY.interpolate({
        inputRange: [0, 60],
        outputRange: [1, 0],
        extrapolate: 'clamp',
    });

    const stickyHeaderOpacity = scrollY.interpolate({
        inputRange: [60, 100],
        outputRange: [0, 1],
        extrapolate: 'clamp',
    });

    return (
        <View style={styles.container}>
            <StatusBar style="light" />

            {/* Background Aura Section */}
            <View style={[styles.headerBackground, { height: headerHeight + 50 }]}>
                <LinearGradient
                    colors={['#7c3aed', '#6366f1', '#4f46e5']}
                    style={StyleSheet.absoluteFill}
                />
                <LinearGradient
                    colors={['rgba(255,255,255,0.25)', 'rgba(255,255,255,0.05)', 'transparent']}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 0.6, y: 0.6 }}
                    style={StyleSheet.absoluteFill}
                />
                <View style={styles.auraContainer}>
                    <View style={[styles.aura, styles.aura1]} />
                    <View style={[styles.aura, styles.aura2]} />
                    <View style={[styles.aura, styles.aura3]} />
                </View>
            </View>

            {/* Mini Sticky Header */}
            <Animated.View
                style={[
                    styles.miniStickyHeader,
                    { paddingTop: insets.top + 5, opacity: stickyHeaderOpacity }
                ]}
                pointerEvents="box-none"
            >
                <LinearGradient
                    colors={['#6366f1', '#4f46e5']}
                    style={StyleSheet.absoluteFill}
                    pointerEvents="none"
                />
                <View style={styles.miniHeaderContent}>
                    <H2 style={styles.miniHeaderTitle} numberOfLines={1}>{listing.business_name}</H2>
                    <TouchableOpacity onPress={() => navigation.goBack()} style={styles.miniBackBtn}>
                        <Ionicons name="chevron-back" size={18} color="#FFFFFF" />
                    </TouchableOpacity>
                </View>
            </Animated.View>

            <Animated.ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.scrollViewContent}
                showsVerticalScrollIndicator={false}
                onScroll={Animated.event(
                    [{ nativeEvent: { contentOffset: { y: scrollY } } }],
                    { useNativeDriver: true }
                )}
                scrollEventThrottle={16}
            >
                {/* Hero Profile Header */}
                <Animated.View style={[styles.heroHeader, { paddingTop: insets.top + 15, opacity: headerOpacity }]}>
                    <View style={styles.topBar}>
                        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backCircle}>
                            <Ionicons name="arrow-back" size={20} color="#FFFFFF" />
                        </TouchableOpacity>
                        <View style={styles.headerIcons}>
                            <TouchableOpacity
                                style={styles.iconCircle}
                                onPress={() => navigation.navigate('Cart')}
                            >
                                <Ionicons name="cart-outline" size={20} color="#FFFFFF" />
                                {getCartCount() > 0 && (
                                    <View style={styles.cartBadge}>
                                        <Caption style={styles.cartBadgeText}>{getCartCount()}</Caption>
                                    </View>
                                )}
                            </TouchableOpacity>
                        </View>
                    </View>

                    <View style={styles.businessHeaderLayout}>
                        <View style={styles.logoFrame}>
                            <Image source={{ uri: getImageUrl(listing.logo_url) }} style={styles.headerLogo} />
                        </View>
                        <View style={styles.businessTitleBlock}>
                            <H2 style={styles.heroTitle} numberOfLines={1}>{listing.business_name}</H2>
                            <View style={styles.heroStatsRow}>
                                <View style={styles.heroStatItem}>
                                    <Ionicons name="star" size={10} color="#F59E0B" />
                                    <Caption style={styles.heroStatText}>{listing.rating || '5.0'}</Caption>
                                </View>
                                <View style={styles.heroStatSeparator} />
                                <Caption style={styles.heroStatText}>{listing.location || 'Local Store'}</Caption>
                            </View>
                        </View>
                    </View>
                </Animated.View>

                {/* Content Surface */}
                <View style={styles.contentSurface}>
                    {/* Description & Contact Surface Card */}
                    <View style={styles.mainInfoCard}>
                        {(() => {
                            const descriptionText = listing.description || 'Welcome to our store! We provide high-quality products and services to our community.';
                            const isLongDescription = descriptionText.length > 150 || (descriptionText.match(/\n/g) || []).length > 2;
                            return (
                                <>
                                    <Text 
                                        style={[styles.infoDescription, { marginBottom: isLongDescription ? 5 : 15 }]}
                                        numberOfLines={isDescriptionExpanded ? undefined : 3}
                                    >
                                        {descriptionText}
                                    </Text>
                                    {isLongDescription && (
                                        <TouchableOpacity 
                                            onPress={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                            style={{ marginBottom: 15 }}
                                        >
                                            <Text style={{ color: '#6366F1', fontSize: 12, fontWeight: '700' }}>
                                                {isDescriptionExpanded ? 'Read less' : 'Read more'}
                                            </Text>
                                        </TouchableOpacity>
                                    )}
                                </>
                            );
                        })()}
                        <View style={styles.infoActionRow}>
                            <TouchableOpacity
                                style={[styles.actionButton, { backgroundColor: '#10B981' }]}
                                onPress={() => handleContact('whatsapp')}
                            >
                                <Ionicons name="logo-whatsapp" size={14} color="#FFF" />
                                <Text style={styles.actionButtonText}>WhatsApp</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.actionButton, { backgroundColor: '#6366F1' }]}
                                onPress={() => handleContact('phone')}
                            >
                                <Ionicons name="call" size={14} color="#FFF" />
                                <Text style={styles.actionButtonText}>Call Shop</Text>
                            </TouchableOpacity>
                        </View>
                    </View>

                    {/* Grouped Categories & Items */}
                    {Object.entries(groupedItems).sort().map(([category, items]) => {
                        const config = ITEM_CATEGORIES[category] || ITEM_CATEGORIES['General'];

                        return (
                            <View key={category} style={styles.categoryWrap}>
                                <View style={styles.categoryHeader}>
                                    <View style={styles.categoryHeaderLeft}>
                                        <View style={[styles.categoryIconCircle, { backgroundColor: config.color + '15' }]}>
                                            <Ionicons name={config.icon} size={14} color={config.color} />
                                        </View>
                                        <Text style={[styles.categoryTitleText, { color: config.color }]}>
                                            {category}
                                        </Text>
                                        <View style={styles.itemCountBadge}>
                                            <Text style={styles.itemCountText}>{items.length}</Text>
                                        </View>
                                    </View>
                                    <TouchableOpacity
                                        style={styles.categorySeeAll}
                                        onPress={() => {/* Navigate to category view */ }}
                                    >
                                        <Text style={[styles.categorySeeAllText, { color: config.color }]}>SEE ALL</Text>
                                        <Ionicons name="chevron-forward" size={10} color={config.color} />
                                    </TouchableOpacity>
                                </View>

                                <ScrollView
                                    horizontal
                                    showsHorizontalScrollIndicator={false}
                                    contentContainerStyle={styles.horizontalProducts}
                                >
                                    {items.map((item) => (
                                        <TouchableOpacity
                                            key={item.id}
                                            style={[styles.productSmallCard, { width: cardWidth }]}
                                            onPress={() => {
                                                if (item.item_type === 'service') {
                                                    navigation.navigate('ServiceDetail', { service: item });
                                                } else {
                                                    navigation.navigate('ProductDetail', { product: item });
                                                }
                                            }}
                                            activeOpacity={0.8}
                                        >
                                            <View style={[styles.productImageFrame, { height: cardWidth }]}>
                                                <Image
                                                    source={{ uri: renderItemImages(item.images || item.image) }}
                                                    style={styles.productImg}
                                                />
                                                <TouchableOpacity
                                                    style={styles.floatingAddBtn}
                                                    onPress={(e) => {
                                                        e.stopPropagation();
                                                        addToCart(item);
                                                    }}
                                                >
                                                    <Ionicons name="add" size={12} color="#FFF" />
                                                </TouchableOpacity>
                                            </View>
                                            <View style={styles.productShortInfo}>
                                                <Text style={styles.productShortName} numberOfLines={1}>{item.name}</Text>
                                                <Text style={styles.productShortPrice}>
                                                    Kes {Number(item.price).toLocaleString()}
                                                </Text>
                                            </View>
                                        </TouchableOpacity>
                                    ))}
                                </ScrollView>
                            </View>
                        );
                    })}

                    {/* Reviews Strip */}
                    {(listing.total_reviews > 0 || listing.recent_reviews) && (
                        <View style={styles.reviewsArea}>
                            <View style={styles.sectionTitleRow}>
                                <H3 style={styles.sectionHeading}>Shop Feedback</H3>
                                <TouchableOpacity style={styles.seeAllBtn}>
                                    <Caption style={styles.seeAllText}>VIEW ALL</Caption>
                                    <Ionicons name="chevron-forward" size={10} color="#F59E0B" />
                                </TouchableOpacity>
                            </View>

                            {listing.recent_reviews ? (
                                listing.recent_reviews.map((rev, idx) => (
                                    <View key={idx} style={styles.reviewBubble}>
                                        <View style={styles.reviewBubbleTop}>
                                            <Text style={styles.reviewerName}>{rev.user_name || 'Verified Buyer'}</Text>
                                            <View style={styles.starRack}>
                                                {[1, 2, 3, 4, 5].map(s => (
                                                    <Ionicons key={s} name="star" size={8} color={s <= rev.rating ? "#F59E0B" : "#E2E8F0"} />
                                                ))}
                                            </View>
                                        </View>
                                        <Text style={styles.reviewComment}>"{rev.comment}"</Text>
                                    </View>
                                ))
                            ) : (
                                <View style={styles.noReviewNotice}>
                                    <Ionicons name="chatbubble-outline" size={20} color="#F59E0B" style={{ marginBottom: 6 }} />
                                    <Text style={styles.noReviewText}>
                                        Join {listing.total_reviews} others who rated this shop.
                                    </Text>
                                </View>
                            )}
                        </View>
                    )}

                    <View style={styles.footerGap} />
                </View>
            </Animated.ScrollView>
            <MarketplaceFooter activeTab="Home" navigation={navigation} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB',
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#F9FAFB',
    },
    headerBackground: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 0,
    },
    auraContainer: {
        ...StyleSheet.absoluteFillObject,
        overflow: 'hidden',
    },
    aura: {
        position: 'absolute',
        borderRadius: 150,
        opacity: 0.15,
    },
    aura1: {
        width: 300,
        height: 300,
        left: -100,
        top: -50,
        backgroundColor: '#C084FC',
    },
    aura2: {
        width: 250,
        height: 250,
        right: -50,
        top: -20,
        backgroundColor: '#818CF8',
    },
    aura3: {
        width: 200,
        height: 200,
        right: 30,
        bottom: -60,
        backgroundColor: '#4C1D95',
    },
    miniStickyHeader: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: 90,
        elevation: 4,
    },
    miniHeaderContent: {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 16,
        height: 44,
        marginTop: 20,
    },
    miniHeaderTitle: {
        color: '#FFFFFF',
        fontSize: 15,
        fontWeight: '800',
        flex: 1,
        textAlign: 'right',
        marginLeft: 40,
    },
    miniBackBtn: {
        width: 32,
        height: 32,
        borderRadius: 16,
        backgroundColor: 'rgba(255,255,255,0.2)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollView: {
        flex: 1,
        zIndex: 1,
    },
    scrollViewContent: {
        paddingBottom: 0,
    },
    heroHeader: {
        paddingHorizontal: 20,
        paddingBottom: 30,
        zIndex: 2,
    },
    topBar: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 15,
    },
    backCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.25)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerIcons: {
        flexDirection: 'row',
        gap: 12,
    },
    iconCircle: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: 'rgba(255,255,255,0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    cartBadge: {
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: '#EF4444',
        width: 16,
        height: 16,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
    },
    cartBadgeText: {
        color: '#FFFFFF',
        fontSize: 8,
        fontWeight: '900',
    },
    businessHeaderLayout: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 15,
    },
    logoFrame: {
        width: 64,
        height: 64,
        borderRadius: 16,
        backgroundColor: '#FFF',
        padding: 4,
        ...getShadow('md'),
    },
    headerLogo: {
        width: '100%',
        height: '100%',
        borderRadius: 12,
        backgroundColor: '#F3F4F6',
    },
    businessTitleBlock: {
        flex: 1,
        paddingBottom: 2,
    },
    heroTitle: {
        color: '#FFFFFF',
        fontSize: 22,
        fontWeight: '800',
        marginBottom: 4,
    },
    heroStatsRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    heroStatItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    heroStatSeparator: {
        width: 3,
        height: 3,
        borderRadius: 2,
        backgroundColor: 'rgba(255,255,255,0.5)',
        marginHorizontal: 8,
    },
    heroStatText: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: 11,
        fontWeight: '700',
    },
    contentSurface: {
        backgroundColor: '#F9FAFB',
        borderTopLeftRadius: 28,
        borderTopRightRadius: 28,
        paddingTop: 24,
        minHeight: SCREEN_HEIGHT - 100,
        zIndex: 5,
        marginTop: -15,
    },
    mainInfoCard: {
        backgroundColor: '#FFFFFF',
        marginHorizontal: 18,
        borderRadius: 18,
        padding: 16,
        marginBottom: 20,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        ...getShadow('xs'),
    },
    infoDescription: {
        fontSize: 12,
        lineHeight: 18,
        color: '#475569',
        marginBottom: 15,
    },
    infoActionRow: {
        flexDirection: 'row',
        gap: 10,
    },
    actionButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        height: 38,
        borderRadius: 12,
        gap: 6,
    },
    actionButtonText: {
        color: '#FFF',
        fontSize: 12,
        fontWeight: '800',
    },
    categoryWrap: {
        marginBottom: 6,
    },
    categoryHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingVertical: 6,
        marginBottom: 0,
    },
    categorySeeAll: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    categorySeeAllText: {
        fontSize: 10,
        fontWeight: '900',
    },
    categoryHeaderLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    categoryIconCircle: {
        width: 32,
        height: 32,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
    },
    categoryTitleText: {
        fontSize: 14,
        fontWeight: '800',
    },
    itemCountBadge: {
        backgroundColor: '#F1F5F9',
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 6,
    },
    itemCountText: {
        fontSize: 10,
        fontWeight: '700',
        color: '#64748B',
    },
    horizontalProducts: {
        paddingHorizontal: 20,
        paddingBottom: 10,
        gap: 10,
    },
    productSmallCard: {
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        overflow: 'hidden',
        ...getShadow('xs'),
    },
    productImageFrame: {
        width: '100%',
        backgroundColor: '#FFFFFF',
        padding: 6,
    },
    productImg: {
        width: '100%',
        height: '100%',
        resizeMode: 'contain',
        borderRadius: 8,
    },
    floatingAddBtn: {
        position: 'absolute',
        bottom: 6,
        right: 6,
        backgroundColor: '#6366F1',
        width: 22,
        height: 22,
        borderRadius: 11,
        alignItems: 'center',
        justifyContent: 'center',
        ...getShadow('sm'),
    },
    productShortInfo: {
        padding: 8,
        borderTopWidth: 1,
        borderTopColor: '#F8FAFB',
    },
    productShortName: {
        fontSize: 10,
        fontWeight: '700',
        color: '#334155',
        marginBottom: 2,
    },
    productShortPrice: {
        fontSize: 11,
        fontWeight: '800',
        color: '#6366F1',
    },
    reviewsArea: {
        marginTop: 10,
        paddingHorizontal: 20,
    },
    sectionTitleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 12,
    },
    sectionHeading: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
    },
    seeAllBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    seeAllText: {
        fontSize: 9,
        fontWeight: '900',
        color: '#F59E0B',
    },
    reviewBubble: {
        backgroundColor: '#FFFFFF',
        borderRadius: 14,
        padding: 12,
        marginBottom: 10,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        ...getShadow('xs'),
    },
    reviewBubbleTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
    },
    reviewerName: {
        fontSize: 12,
        fontWeight: '700',
        color: '#334155',
    },
    starRack: {
        flexDirection: 'row',
        gap: 2,
    },
    reviewComment: {
        fontSize: 11,
        color: '#64748B',
        fontStyle: 'italic',
        lineHeight: 16,
    },
    noReviewNotice: {
        backgroundColor: '#FFFBEB',
        borderRadius: 14,
        padding: 20,
        alignItems: 'center',
        borderWidth: 1,
        borderColor: '#FEF3C7',
    },
    noReviewText: {
        fontSize: 11,
        color: '#B45309',
        textAlign: 'center',
        fontWeight: '700',
    },
    footerGap: {
        height: 40,
    }
});

export default BusinessDetailScreen;
